import { Fragment, useState, useEffect, useMemo, useCallback } from "react";
import { Card, Button, Dropdown, Menu, Affix, Popconfirm, Typography, Space, Row, Col, Statistic } from "antd";
import ListTable from "common/ListTable";
import Header from "../../Common/Header";
import { isMobileView } from "../../Common/AppHelper";
import { Link, useLocation } from "react-router-dom";
import { MoreOutlined, EditOutlined, EyeOutlined, DeleteTwoTone } from "@ant-design/icons";
import {
  IMPORT_PI_LIST,
  SEND_PI_NOTIFICATION,
  IMPORT_PI_SALES_CONTRACT_SUMMARY,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, deleteData, postData } from "../../../apiServices/common";
import CustomFilter from "../../Common/CustomFilter";
import { cglFormatDate, commaSeparateNumber, hasPermission } from "../../../utils/functions";
import {
  PI_VIEW_PERMISSION,
  PI_CREATE_PERMISSION,
  PI_EDIT_PERMISSION,
  PI_DELETE_PERMISSION,
} from "../../../routes/permissions";
import { alertPop } from "../../../apiServices/common/helper";
import { useNavigate } from "react-router-dom";

const Forwarder = (props) => {
  const { width } = props;

  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [permitList, setPermitList] = useState();
  const [totalData, setTotalData] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rangeData, setRangeData] = useState();
  const [selectedIds, setSelectedIds] = useState(null);
  const [searchValues, setSearchValues] = useState();
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const { Title } = Typography;

  const navigate = useNavigate();

  useEffect(() => {
    const handleBeforeUnload = () => {
      const filterValues = localStorage.getItem("import_pi_filter_value");
      if (filterValues) {
        const parsedFilters = JSON.parse(filterValues);
        if (parsedFilters.sales_contract_id) {
          delete parsedFilters.sales_contract_id;
          localStorage.setItem("import_pi_filter_value", JSON.stringify(parsedFilters));
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const columns = [
    {
      title: "Sales Contract No",
      dataIndex: ["sale_contract_info", "reference_no"],
      key: ["sale_contract_info", "reference_no"],
    },
    {
      title: "Supplier Name",
      dataIndex: ["supplier_info", "name"],
      key: ["supplier_info", "name"],
      width: "20%",
    },
    {
      title: "PI Number",
      dataIndex: ["pi_number"],
      key: ["pi_number"],
    },
    {
      title: "PI Value",
      dataIndex: ["pi_value"],
      key: ["pi_value"],
      render: (value) => <span>{commaSeparateNumber(value?.toFixed(2))}</span>,
    },

    {
      title: "PI Date",
      dataIndex: ["created_at"],
      key: "created_at",
      render: (value) => cglFormatDate(value),
    },
    {
      title: "B2B LC Date",
      dataIndex: ["b2b_lc_date"],
      key: "b2b_lc_date",
      render: (value) => cglFormatDate(value),
    },
    {
      title: "Status",
      dataIndex: ["status"],
      key: ["status"],
    },
    {
      title: "",
      key: "action",
      width: "10%",
      align: "right",
      render: (_, record) => (
        <div className="cursore-pointer">
          <Dropdown overlay={menu(record)}>
            <MoreOutlined style={{ fontSize: "20px" }} />
          </Dropdown>
        </div>
      ),
    },
  ];

  const fetchSummaryData = useCallback(async (salesContractId) => {
    if (!salesContractId) {
      setSummaryData(null);
      return;
    }

    setSummaryLoading(true);
    try {
      const response = await getData(IMPORT_PI_SALES_CONTRACT_SUMMARY, false, {
        sales_contract_id: salesContractId,
      });

      if (response && response?.status === 200) {
        setSummaryData(response.data.data);
      } else {
        setSummaryData(null);
      }
    } catch (error) {
      console.error("Error fetching summary:", error);
      setSummaryData(null);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const getAllPermitList = async (passedObject) => {
    const filter = passedObject;

    if (filter?.sales_contract_id) {
      await fetchSummaryData(filter.sales_contract_id);
    } else {
      setSummaryData(null);
    }

    let res = await getData(IMPORT_PI_LIST, false, filter);

    if (res) {
      let masterData = res?.data;
      const PIList = masterData?.data?.data?.map((item) => {
        return {
          ...item,
          key: item.id,
        };
      });
      setPermitList(PIList);
      setTotalData(masterData?.data?.total);
      setRangeData(masterData?.data?.per_page);
    }
    setLoading(false);
  };

  useEffect(() => {
    const filterValues = localStorage.getItem("import_pi_filter_value")
      ? JSON.parse(localStorage.getItem("import_pi_filter_value"))
      : null;
    setLoading(true);

    getAllPermitList({
      page: currentPage,
      ...(filterValues && { ...filterValues }),
    });
  }, [currentPage, searchValues]);

  const removePi = async (value) => {
    let res = await deleteData(`/api/accounting/import-pi/delete/${value}`);
    window.location.reload();
  };

  const menu = (data) => (
    <Menu
      items={[
        {
          label: (
            <Link to={`/import/edit-pi/${data.id}`}>
              <EditOutlined /> Edit
            </Link>
          ),
          key: "0",
          disabled: !hasPermission([PI_EDIT_PERMISSION]),
        },
        {
          label: (
            <Link to={`/import/view-pi/${data.id}`}>
              <EyeOutlined /> View
            </Link>
          ),
          key: "1",
          disabled: !hasPermission([PI_VIEW_PERMISSION]),
        },
        {
          label: (
            <Popconfirm title="Are you sure you want to delete?" onConfirm={() => removePi(data.id)}>
              <DeleteTwoTone /> Delete
            </Popconfirm>
          ),
          key: "2",
          disabled: !hasPermission([PI_DELETE_PERMISSION]),
        },
      ]}
    />
  );
  const paginate = (page) => {
    setCurrentPage(page);
  };

  const handleFilter = () => {
    setShowFilter((prev) => !prev);
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedIds(selectedRowKeys);
    },
    getCheckboxProps: (record) => {
      const lcStatus = ["Approved"];
      return {
        disabled: !lcStatus.includes(record.status),
      };
    },
  };

  const requestForB2BLC = async () => {
    if (!selectedIds || selectedIds?.length <= 0) {
      return null;
    }

    const query = SEND_PI_NOTIFICATION;
    const bodyData = {
      id: selectedIds,
    };

    const response = await postData(query, bodyData);

    if (response && response?.code === 200) {
      alertPop("success", response?.message || "Request sent Successfully");
      setSelectedIds(null);
    } else if (response && response?.code === 422) {
      alertPop("error", response?.message || "PI Documents must be attached");
      navigate("/import/pi");
    }
  };

  const extra = (
    <Space>
      <Button
        type={showFilter ? "danger" : "primary"}
        size="small"
        onClick={() => setShowFilter((prev) => !prev)}
        style={{ float: "right" }}
      >
        {`${showFilter ? "Close" : "Open"} Filter Option`}
      </Button>
      <Button key="3" size="small" type="primary" disabled={!hasPermission([PI_CREATE_PERMISSION])}>
        <Link to="/import/add-pi">{hasPermission([PI_CREATE_PERMISSION]) ? "Add New" : "No Permission"}</Link>
      </Button>
      <Button
        key="4"
        size="small"
        type="primary"
        disabled={!hasPermission([PI_CREATE_PERMISSION]) || !selectedIds || selectedIds?.length <= 0}
        onClick={requestForB2BLC}
      >
        {hasPermission([PI_CREATE_PERMISSION]) ? "Request for B2B LC" : "No Permission"}
      </Button>
    </Space>
  );

  const memoizedCustomFilter = useMemo(
    () => (
      <CustomFilter
        showFilter={showFilter}
        tableData={permitList}
        fetchTableData={getAllPermitList}
        filterName="import_pi"
      />
    ),
    [showFilter],
  );

  return (
    <Fragment>
      <Affix offsetTop={0}>
        <Card className="p-fixed" style={{ width: `${isMobileView() ? "100%" : `${width}px`}` }}>
          <Header title={<Title level={5}>Proforma Invoice</Title>} subTitle="" actions={extra} />
        </Card>
      </Affix>

      <div className="containt-body">
        <Card>
          {memoizedCustomFilter}

          {/* Summary Data Section */}
          {summaryData && (
            <Card style={{ marginBottom: 16 }} loading={summaryLoading} title="Sales Contract Summary" bordered={false}>
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Total Import PI Amount"
                    value={commaSeparateNumber(parseFloat(summaryData.sumSCImportPI || 0).toFixed(2))}
                    precision={2}
                    suffix="USD"
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Total Sum of B2B LC"
                    value={commaSeparateNumber(parseFloat(summaryData.sumSCB2BLc || 0).toFixed(2))}
                    precision={2}
                    suffix="USD"
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Remaining"
                    value={commaSeparateNumber(parseFloat(summaryData.restOpenPIvalue || 0).toFixed(2))}
                    precision={2}
                    suffix="USD"
                  />
                </Col>
              </Row>
            </Card>
          )}

          <ListTable
            loading={loading}
            dataSource={permitList}
            columns={columns}
            pagination={{
              showSizeChanger: false,
              current: currentPage,
              total: totalData,
              pageSize: rangeData,
              onChange: (page) => paginate(page),
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
            rowSelection={{
              type: "checkbox",
              ...rowSelection,
            }}
          />
        </Card>
      </div>
    </Fragment>
  );
};

export default Forwarder;
